import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';

export default function Iframe({
  src,
  title,
  className,
  style,
  width,
  height: providedHeight,
  ...props
}) {
  const [loading, setLoading] = useState(true);
  const [dynamicHeight, setDynamicHeight] = useState(null);
  const iframeRef = useRef(null);

  // Calculate aspect ratio for fallback responsive sizing
  const aspectRatio = width && providedHeight ? providedHeight / width : null;

  const updateHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      // For same-origin iframes, read document height directly
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        const newHeight = Math.max(
          doc.body?.scrollHeight || 0,
          doc.documentElement?.scrollHeight || 0
        );
        if (newHeight > 0) {
          setDynamicHeight(newHeight);
        }
      }
    } catch (e) {
      // Cross-origin - rely on postMessage from embedded site
    }
  }, []);

  useEffect(() => {
    // Listen for height messages from embedded iframes
    const handleMessage = (event) => {
      if (event.data?.type === 'iframeHeight' && event.data?.height) {
        setDynamicHeight(event.data.height);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleLoad = () => {
    setLoading(false);
    updateHeight();

    // Set up MutationObserver for same-origin content changes
    const iframe = iframeRef.current;
    try {
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (doc) {
        const observer = new MutationObserver(updateHeight);
        observer.observe(doc.body, {
          childList: true,
          subtree: true,
          attributes: true,
        });
        iframe.contentWindow?.addEventListener('resize', updateHeight);
      }
    } catch (e) {
      // Cross-origin - can't observe
    }
  };

  // Priority: dynamic height > aspect ratio fallback > provided height > default
  const useDynamicHeight = dynamicHeight !== null;
  const useAspectRatio = !useDynamicHeight && aspectRatio;

  return (
    <Box
      className={className}
      sx={{
        position: 'relative',
        width: '100%',
        ...(useDynamicHeight
          ? { height: `${dynamicHeight}px` }
          : useAspectRatio
            ? { paddingBottom: `${aspectRatio * 100}%`, height: 0 }
            : { height: providedHeight ? `${providedHeight}px` : '500px' }),
        ...style,
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            zIndex: 1,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      <iframe
        ref={iframeRef}
        src={src}
        title={title || 'Embedded content'}
        onLoad={handleLoad}
        allow="microphone; autoplay; encrypted-media"
        style={{
          position: useAspectRatio ? 'absolute' : 'relative',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
        }}
        {...props}
      />
    </Box>
  );
}
